import React, { useState, useEffect } from 'react';
import {
    Form,
    Input,
    Select,
    Switch,
    Button,
    Row,
    Col,
    InputNumber
} from 'antd';
import { Editor } from "@tinymce/tinymce-react";
import tinyMCEConfig from "../tinymce/tinyMCEConfig";
import FileUpload from "../common/fileUploadComponent";
import { NAIRA } from "../../defaults/utilities";
import { userStore } from "../../store/userStore";
import { CREATE_MEMO } from "../../helpers/roles";

const { Option } = Select;

const MemoForm = ({
    memo,
    content,
    setContent,
    memoContentMode,
    setMemoContentMode,
    financialYears,
    structures,
    units,
    fyear,
    setFyear,
    category,
    setCategory,
    amount,
    setAmount,
    unit,
    setUnit,
    file,
    setFile,
    memoType,
    setMemoType,
    handleSubmit,
    handleUpdate,
    titleRef,
    memoReference,
    staffId
}) => {
    const currentUser = userStore(state => state.user);
    const [title, setTitle] = useState(memo?.title ?? '');
    const [form] = Form.useForm();

    useEffect(() => {
        if (!currentUser?.roles?.includes(CREATE_MEMO) && staffId?.current)
            staffId.current.input.value = currentUser.staffId;
    }, [currentUser, staffId]);

    useEffect(() => {
        setTitle(memo?.title ?? '');
        
        if (memo?.id) {
            form.setFieldsValue({
                title: memo?.title ?? '',
                staffId: memo?.owner ?? '',
                fyear: memo?.financialYear?.id ?? null,
                memoReference: memo?.memoReference ?? '',
                category: memo?.category?.id ?? null,
                amount: memo?.amount ?? 0,
                unit: memo?.unit?.id ?? null,
                memoType: memo?.memoType ?? 'ADVANCE',
            });
        } else {
            form.resetFields();
            setTitle('');
        }
    }, [memo, form]);

    const url = process.env.REACT_APP_SERVER_URL || ""

    const addFile = (f) => {
        setFile(f);
    };

    const [previewContent, setPreviewContent] = useState(content);
    const [editorInitialContent, setEditorInitialContent] = useState("");

    useEffect(() => {
        setPreviewContent(content);
    }, [content]);

    useEffect(() => {
        setEditorInitialContent(content || "");
    }, [memo?.id]);
    
    const isFormValid = fyear && title?.trim() && amount > 0;

    const updateContent = (newContent) => {
        const sig = "<img src=\"" + url + "/signature/fetch/"+ currentUser?.userId + "\" alt=\"signature\" width=\"120px\" height=\"80px\"/>";
        const t = newContent?.replaceAll("{signature}", sig)
        setContent(newContent)
        setPreviewContent(t)
    }
    return (
        <Form
            form={form}
            style={{ marginTop: "2em" }}
            layout={'vertical'}
            initialValues={{
                title: '',
                staffId: !currentUser?.roles?.includes(CREATE_MEMO) ? currentUser?.staffId : '',
                fyear: null,
                memoReference: '',
                category: null,
                amount: 0,
                unit: null,
                memoType: 'ADVANCE',
            }}
        >
            <Row gutter={2}>
                <Col span={12}>
                    <Form.Item name="title" style={{ marginBottom: "15px" }}>
                        <Input 
                            placeholder="Enter memo title" 
                            ref={titleRef} 
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </Form.Item>
                </Col>
                <Col span={12}>
                    <Form.Item name="staffId" style={{ marginBottom: "15px" }}>
                        <Input
                            placeholder="Enter staffId"
                            ref={staffId}
                            disabled={!currentUser?.roles?.includes(CREATE_MEMO)}
                        />
                    </Form.Item>
                </Col>
            </Row>

            <Row gutter={2}>
                <Col span={8}>
                    <Form.Item style={{ marginBottom: "2em" }} name="fyear">
                        <Select placeholder="Select Financial year" onChange={(e) => setFyear(e)}>
                            {
                                financialYears.map((year, idx) => (
                                    <Option value={year.id} key={idx}>{year.value}</Option>
                                ))
                            }
                        </Select>
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item style={{ marginBottom: "2em" }} name="memoType">
                        <Select placeholder="Select Memo Type" onChange={(e) => setMemoType(e)}>
                            <Option value="CLAIM">CLAIM</Option>
                            <Option value="ADVANCE">ADVANCE</Option>
                        </Select>
                    </Form.Item>
                </Col>

                <Col span={8}>
                    <Form.Item style={{ marginBottom: "2em" }} name="amount">
                        <InputNumber
                            style={{ width: '100%' }}
                            prefix={NAIRA}
                            placeholder="Memo Amount"
                            onChange={(e) => setAmount(e)}
                        />
                    </Form.Item>
                </Col>
            </Row>

            {/* Switch between file and content upload */}
            {
                currentUser?.roles?.includes(CREATE_MEMO) && (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        marginBottom: "2em"
                    }}>
                        <Switch
                            onChange={(e) => setMemoContentMode(!e)}
                            checkedChildren="File mode"
                            disabled={Object.keys(memo).length > 0}
                            unCheckedChildren="Content mode"
                            defaultChecked={Object.keys(memo).length < 1 ? false : !!(memo.documentPath)}
                        />
                    </div>
                )
            }

            {/* File upload */}
            {
                <Row gutter={2}>
                    {/* <Col span={12}>
                        <Form.Item name="memoReference" style={{ marginBottom: "15px" }}>
                            <Input placeholder="Enter memo reference" ref={memoReference} />
                        </Form.Item>
                    </Col> */}
                    <Col span={12}>
                        <FileUpload cb={addFile} placeholder="Select attachment if any" />
                    </Col>
                </Row>
            }

            {/* Content editor */}
            {
                <>
                <h3 style={{ color: "#000000" }}>Memo Content</h3>
                <Editor
                    initialValue={editorInitialContent}
                    onEditorChange={(newContent) => updateContent(newContent)}
                    init={tinyMCEConfig}
                    tinymceScriptSrc={tinyMCEConfig.source}
                />
                <h3 style={{ color: "#000000" }}>Preview</h3>
                <div
                    style={{
                        marginBottom: "2em",
                        padding: "10px",
                        border: "1px solid #d9d9d9",
                        borderRadius: "4px",
                        minHeight: "150px"
                    }}
                    className="editor-preview"
                    dangerouslySetInnerHTML={{ __html: previewContent }}
                />
            </>
            }

            {/* Submit buttons */}
            {
                memo?.id ? (
                    <Form.Item
                        wrapperCol={{
                            span: 8,
                            offset: 22
                        }}
                        className="submit-button-wrapper"
                    >
                        <Button type="primary" onClick={handleUpdate} disabled={!isFormValid}>
                            Update
                        </Button>
                    </Form.Item>
                ) : (
                    <Form.Item
                        wrapperCol={{
                            span: 8,
                            offset: 22
                        }}
                        className="submit-button-wrapper"
                    >
                        <Button type="primary" onClick={handleSubmit} disabled={!isFormValid}>
                            Create
                        </Button>
                    </Form.Item>
                )
            }
        </Form>
    );
};

export default MemoForm;
